import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Input, IconButton } from "@bloomberg/media.atomics";
import { Search, Add, Check } from "@bloomberg/media.icons";
import styles from "./styles.module.css";
import {
  useCurrentWatchlist,
  useSelectedPortfolioId,
  useUserId,
  WatchlistContext,
} from "~context/WatchlistContext";
import { watchlistClient } from "~config/octagon-client";
import { useTickerSearch } from "~hooks/useTickerSearch";
import type { SearchQueryTicker } from "~types/types";

interface TickerSearchResultItemProps {
  readonly ticker: SearchQueryTicker;
  readonly isAdded: boolean;
  readonly onAdd: (ticker: SearchQueryTicker) => void;
}

function TickerSearchResultItem({
  ticker,
  isAdded,
  onAdd,
}: TickerSearchResultItemProps): React.ReactElement {
  const handleClick = useCallback(() => {
    if (!isAdded) {
      onAdd(ticker);
    }
  }, [ticker, isAdded, onAdd]);

  return (
    <div className={styles.resultItem} role="option" aria-selected={isAdded}>
      <div className={styles.resultItemText}>
        <span className={styles.resultItemName}>{ticker.name}</span>
        <span className={styles.resultItemSymbol}>{ticker.resource_id}</span>
      </div>
      <IconButton
        label={isAdded ? "Added" : "Add"}
        size="medium"
        variant="outline"
        onClick={handleClick}
        disabled={isAdded}
      >
        {isAdded ? <Check /> : <Add />}
      </IconButton>
    </div>
  );
}

export default function TickerSearch(): React.ReactElement {
  const currentWatchlist = useCurrentWatchlist();
  const userId = useUserId();
  const selectedPortfolioId = useSelectedPortfolioId();
  const store = useContext(WatchlistContext);

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [addedTickers, setAddedTickers] = useState<Set<string>>(new Set());

  const excludedTickers = useMemo(
    () =>
      currentWatchlist?.positions?.map((p: { ticker: string }) => p.ticker) ??
      [],
    [currentWatchlist?.positions]
  );

  const { results, isLoading } = useTickerSearch({
    searchQuery: query,
    excludedTickers,
  });

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);

    if (!value.trim()) {
      setIsOpen(false);

      return;
    }

    setIsOpen(true);
  }, []);

  const handleAddTicker = useCallback(
    async (ticker: SearchQueryTicker) => {
      if (!userId || !selectedPortfolioId) return;

      try {
        const client = watchlistClient.getInstance();

        await client.addHoldings(userId, selectedPortfolioId, [
          { ticker: ticker.resource_id },
        ]);

        setAddedTickers((prev) => new Set(prev).add(ticker.resource_id));

        if (store) {
          const { refreshCurrentWatchlist } = store.getState();

          await refreshCurrentWatchlist();
        }
      } catch {
        // Silently fail - user can retry
      }
    },
    [userId, selectedPortfolioId, store]
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={styles.tickerSearchContainer} ref={containerRef}>
      <Input
        labelText="Add a ticker"
        variant="outline-label"
        size="small"
        value={query}
        onChange={handleQueryChange}
        icon={<Search />}
      />
      {isOpen && (
        <div className={styles.resultsDropdown} role="listbox">
          {isLoading && results.length === 0 && (
            <div className={styles.statusMessage}>Searching...</div>
          )}
          {!isLoading && results.length === 0 && query.trim() && (
            <div className={styles.statusMessage}>
              No results found. Try different keywords.
            </div>
          )}
          {results.map((ticker) => (
            <TickerSearchResultItem
              key={ticker.resource_id}
              ticker={ticker}
              isAdded={addedTickers.has(ticker.resource_id)}
              onAdd={handleAddTicker}
            />
          ))}
        </div>
      )}
    </div>
  );
}
